import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';

import { RichTextSwitch } from 'src/containers';
import { useCmsMode } from 'src/contexts/ModeContext/ModeContext';
import { useComponentTheme } from 'src/hooks/useComponentTheme';
import ThemeProvider from 'threads5/themes/theme-provider';
import { useMemo } from 'react';

import { makeStructuredData } from './CmsFaq.utils';
import Section from 'src/components-v2/Layout/Section/Section';
import Container from 'src/components-v2/Layout/Container/Container';
import Row from 'src/components-v2/Layout/Grid/Row';
import Column from 'src/components-v2/Layout/Grid/Column';
import { LeftColumn } from './LeftColumn';
import {
  FaqDetails,
  FaqDetailsContent,
  FaqDetailsSummary,
  Strip,
  FaqIcons,
} from './Faq.styled';
import { CmsFaqProps } from './Faq.types';
import Head from 'next/head';

const CmsFaq: React.FC<CmsFaqProps> = ({ fields, sys }) => {
  const mode = useCmsMode({
    mode: fields?.mode,
    backgroundColor: fields?.backgroundColor,
  });
  const componentTheme = useComponentTheme(mode);

  const structuredData = useMemo(() => {
    return makeStructuredData(
      fields?.faqItems?.map((item) => {
        return {
          question: item.fields.question,
          plainTextAnswer: documentToPlainTextString(item.fields.answer),
          styles: item.fields.styles,
          sys: item.sys,
        };
      }),
    );
  }, [fields?.faqItems]);

  return (
    <ThemeProvider theme={componentTheme}>
      <Section>
        <Container>
          <Row>
            <LeftColumn sys={sys} fields={{ body: fields?.body }} />
            <Column xs={24} md={12} lg={11} lgOffset={1}>
              {fields?.faqItems?.map((item) => {
                return (
                  <FaqDetails key={item.sys.id} name={sys?.id}>
                    <FaqDetailsSummary>
                      <h3>{item.fields.question}</h3>
                      <FaqIcons aria-hidden='true'>
                        <span className='faq-icon-bar faq-icon-bar--vertical' />
                        <span className='faq-icon-bar faq-icon-bar--horizontal' />
                      </FaqIcons>
                    </FaqDetailsSummary>
                    <FaqDetailsContent>
                      <RichTextSwitch
                        content={item.fields.answer}
                        sys={item.sys}
                        nodes={{
                          p: {
                            variant: 'p',
                            component: 'p',
                            fieldId: 'answer',
                          },
                        }}
                      />
                    </FaqDetailsContent>
                    <Strip />
                  </FaqDetails>
                );
              })}
            </Column>
          </Row>
        </Container>
        {fields?.includeFaqSchema && (
          <Head>
            <script
              key='faq-schema'
              type='application/ld+json'
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(structuredData),
              }}
            />
          </Head>
        )}
      </Section>
    </ThemeProvider>
  );
};

export default CmsFaq;
